import React from 'react';
import { Controlled as ReactCodeMirror } from 'react-codemirror2';
import cancelEvent from '../../../services/mini/cancelEvent';
import getCodeMirrorInstance from '../../../services/mini/getCodeMirrorInstance';
import { isValidJSON, getInvalidJSONMessage } from '../../../services/mini/isValidJSON';
require('codemirror/mode/javascript/javascript');
require('codemirror/addon/edit/matchbrackets.js');

const defaultTabSize = 2;
const defaultIndentUnit = 2;

const options = {
    lineNumbers: false,
    readOnly: false,
    lint: true,
    matchBrackets: true,
    indentWithTabs: false,
    tabSize: defaultTabSize,
    indentUnit: defaultIndentUnit,
    autoCloseBrackets: true,
    autoCloseTags: true,
    maxHighlightLength: 500,
    showCursorWhenSelecting: true,
};

class TextFieldWithJsonWrapper extends React.Component {
    constructor() {
        super();
        this.state = {
            focused: false,
            validationMessage: null
        };

        this.editor = React.createRef();
    }

    componentDidMount() {
        this.validate(this.props.value);
        const scriptEditor = this.getScriptEditorCm();
        if (!this.props.noAutoFocus) {
            if (!scriptEditor) return;
            scriptEditor.focus();
        }
        if (this.props.withCursorInsideBrackets) {
            this.setCursorInsideBrackets();
        }
        this.addHotKeys();
    }

    addHotKeys = () => {
        const scriptEditor = this.getScriptEditorCm();
        if (!scriptEditor) return;
        scriptEditor.setOption('extraKeys', {
            'Cmd-Z': this.undo,
            'Ctrl-Z': this.undo,
        });
    }

    undo = () => {
        const { onUndo } = this.props;
        const scriptEditor = this.getScriptEditorCm();
        if (scriptEditor) {
            scriptEditor.undo();
        }
        if (onUndo) {
            onUndo();
        }
    }

    setCursorInsideBrackets = () => {
        const scriptEditor = this.getScriptEditorCm();
        if (!scriptEditor) return;
        const doc = scriptEditor.getDoc();
        const content = doc.getValue();
        const position = content.indexOf('{}');

        if (position !== -1) {
            const before = content.slice(0, position);
            const lines = before.split('\n');
            const line = lines.length - 1;
            const ch = lines[lines.length - 1].length + 1;
            scriptEditor.focus();
            doc.setCursor({ line: line, ch: ch });
        }
    }

    onChange = (value) => {
        const { onChange } = this.props;
        onChange(value);
        this.validate(value);
    }

    onKeyDown = (e) => {
        if (this.props.onShiftEnter) {
            if (e.which === 13 && e.shiftKey) {
                if (e.shiftKey) {
                    this.props.onShiftEnter(this.props.value);
                    cancelEvent(e);
                }
            }
        }
    }

    validate(val) {
        const { onValidate, skipJsonPreparation, validationMessage, skipValidation } = this.props;
        const validationResult = skipValidation ? true : isValidJSON(val, skipJsonPreparation) || val === '';

        if (validationResult) {
            this.setState({ validationMessage: '' });
        } else {
            this.setState({ validationMessage: getInvalidJSONMessage(validationMessage, val, skipJsonPreparation) });
        }

        if (onValidate) {
            onValidate(!!validationResult);
        }

        return validationResult;
    }

    getScriptEditorCm = () => {
        return getCodeMirrorInstance(this.editor.current);
    }

    render() {
        const { value, className, placeholder, readOnly, skipValidation, noBorder, mode, tabSize, indentUnit } = this.props;
        const { focused, validationMessage } = this.state;
        const invalidClass = skipValidation !== true ? (validationMessage && 'invalid') : '';
        const optionsForEditor = {
            ...options,
            readOnly: readOnly ? true : false,
            tabSize: tabSize ? tabSize : defaultTabSize,
            indentUnit: indentUnit ? indentUnit : defaultIndentUnit,
        };

        return (
            <div className={`text-field-with-json-wrapper ${className} ${noBorder && 'no-border'}`}>
                {!skipValidation && <div className={`text-field-with-json-wrapper_label ${(!focused && value) && 'visible-with-opacity'} ${invalidClass}`}>
                    {validationMessage}
                </div>}
                <div className={`text-field-with-json-wrapper_text ${invalidClass}`}>
                    <ReactCodeMirror
                        ref={this.editor}
                        value={value}
                        options={{ ...optionsForEditor, mode: mode || 'application/json' }}
                        onBeforeChange={(editor, data, value) => {
                            this.onChange(value);
                        }}
                        onFocus={(editor, data, value) => {
                            this.setState({ focused: true });
                        }}
                        onKeyDown={(editor, event) => {
                            this.onKeyDown(event);
                        }}
                        onBlur={(editor, data, value) => {
                            this.setState({ focused: false });
                            this.validate(this.props.value);
                        }}
                    />
                </div>
                <div
                    className={`text-field-with-json-wrapper_placeholder ${!value && 'empty'} ${focused && 'focused'}`}>
                    {placeholder}
                </div>
            </div>
        );
    }
}

export default TextFieldWithJsonWrapper;
